import { Card } from '@saay/shadcn/components/card';
import { cn } from '@saay/shadcn/lib/utils';
import { BrandPsi } from '@saay/ui';
import type { ReactNode } from 'react';

/**
 * The SAAY card face (olive with the gold Ψ, D-80): one per screen, for the figure the user opened
 * the app for (buyer: purchasable credit; merchant: next payout; admin: next settlement batch).
 */
export function SignatureCard({
  label,
  value,
  badge,
  header,
  children,
  className,
}: {
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly badge?: ReactNode;
  /** Content above the label (the buyer card's logo row). */
  readonly header?: ReactNode;
  readonly children?: ReactNode;
  readonly className?: string;
}) {
  return (
    <Card
      className={cn(
        'relative gap-0 rounded-[28px] bg-[linear-gradient(135deg,#0f3a1f_0%,var(--brand)_55%,#0b2412_100%)] p-6 text-white ring-0 shadow-[0_18px_40px_-18px_rgba(0,50,10,0.55)]',
        className,
      )}
    >
      <BrandPsi className="pointer-events-none absolute -bottom-12 -start-8 size-52 text-brand-gold/10" />
      {header === undefined ? null : <div className="relative mb-6">{header}</div>}
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm text-white/70">{label}</div>
          <div className="mt-2 text-[32px] leading-none font-extrabold tabular-nums">{value}</div>
        </div>
        {badge === undefined ? null : (
          <span className="shrink-0 rounded-full bg-brand-gold/20 px-3 py-1.5 text-xs font-semibold text-[#e3cf7f]">
            {badge}
          </span>
        )}
      </div>
      {children === undefined ? null : <div className="relative mt-5">{children}</div>}
    </Card>
  );
}

/** The «تومان» unit on the card face. */
export function CardFaceUnit() {
  return <span className="ms-1 text-sm font-medium text-white/70">تومان</span>;
}

/** Label/value pairs in a translucent strip on the card face. */
export function CardFaceStats({
  items,
}: {
  readonly items: readonly { readonly label: string; readonly value: ReactNode }[];
}) {
  return (
    <dl className="grid grid-flow-col gap-3 rounded-2xl bg-white/[0.07] p-4 text-sm">
      {items.map((item) => (
        <div key={item.label}>
          <dt className="text-xs text-white/60">{item.label}</dt>
          <dd className="mt-1 font-bold tabular-nums">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Label/value rows on the card face, divided by hairlines. */
export function CardFaceRows({
  items,
}: {
  readonly items: readonly { readonly label: string; readonly value: ReactNode }[];
}) {
  return (
    <dl className="flex flex-col gap-2.5 text-sm">
      {items.map((item) => (
        <div
          className="flex items-center justify-between border-b border-white/10 pb-2.5 last:border-0"
          key={item.label}
        >
          <dt className="text-white/60">{item.label}</dt>
          <dd className="font-bold">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
